import { BackgroundSettings, BackgroundType } from "@/shared/types";
import { useResizeObserver } from "@/shared/utils/useResizeObserver";
import React from "react";
import ReactPlayer from "react-player";

interface VideoBackgroundProps {
  sectionContainerRef: React.RefObject<HTMLDivElement>;
  backgroundSettings: BackgroundSettings;
}

export const VideoBackground: React.FC<VideoBackgroundProps> = ({
  sectionContainerRef,
  backgroundSettings,
}) => {
  const {
    observedSize: { width, height },
  } = useResizeObserver(sectionContainerRef);

  const wrapperWidth = width ?? 1;
  const wrapperHeight = height ?? 1;

  if (backgroundSettings?.type !== BackgroundType.Video) return null;

  const VideoReactPlayer =
    typeof ReactPlayer === "function"
      ? ReactPlayer
      : (ReactPlayer as any).default;

  const backgroundVideo = backgroundSettings?.video;

  // if the required ratio is less than 1 ->
  // - we need to switch between the divisions and multiplications
  const wrapperRatio = wrapperWidth ?? 1 / wrapperHeight ?? 1;

  let videoWidth;
  let videoHeight;

  if (wrapperRatio > 1) {
    const requiredRatio = 16 / 9;
    videoWidth = wrapperWidth;
    videoHeight = wrapperWidth / requiredRatio;

    if (videoHeight < wrapperHeight) {
      videoHeight = wrapperHeight;
      videoWidth = videoHeight * requiredRatio;
    }
  } else {
    // const requiredRatio = 9 / 16; // video not cropped but has black spaces
    const requiredRatio = 16 / 9; // video take fullscreen but cropped
    videoWidth = wrapperHeight * requiredRatio;
    videoHeight = wrapperHeight;

    if (videoWidth < wrapperWidth) {
      videoWidth = wrapperWidth;
      videoHeight = videoWidth * requiredRatio;
    }
  }

  const top = (videoHeight - wrapperHeight) / 2;
  const left = (videoWidth - wrapperWidth) / 2;

  return (
    <VideoReactPlayer
      url={backgroundVideo?.src}
      muted
      loop
      playing
      width={`${videoWidth}px`}
      height={`${videoHeight}px`}
      playsinline
      style={{
        position: "absolute",
        minWidth: "100%",
        minHeight: "100%",
        pointerEvents: "none",
        right: -Math.abs(left),
        bottom: -Math.abs(top),
        top: -Math.abs(top),
        left: -Math.abs(left),
      }}
      config={{
        // https://developers.google.com/youtube/player_parameters?playerVersion=HTML5
        youtube: {
          playerVars: {
            showinfo: 0,
            controls: 0,
            modestbranding: 1,
            cc_load_policy: 0,
            disablekb: 1,
            iv_load_policy: 3,
          },
        },
      }}
    />
  );
};
